import { api } from '~/services/api'
import type { ProductFacetGroupType, ProductKindType, ProductSpecType } from '~/services/product.service'

/**
 * Catálogo público: lo que ve un visitante sin sesión.
 *
 * La empresa va en la ruta porque `key` sólo es único dentro de una empresa, a
 * diferencia del slug de una página del constructor, que sí es global. El
 * backend devuelve una versión recortada del producto (sin coste ni margen), de
 * ahí que los tipos no se reutilicen de `product.service`.
 */

export interface PublicVariantType {
  id: number
  sku?: string | null
  options: Record<string, string>
  /** Nulo = usa el precio del producto. */
  price?: number | null
  stock: number
}

export interface PublicPriceRowType {
  id?: number
  variantId: number | null
  name: string
  minQuantity: number
  amount: number
  isDefault?: boolean
}

export interface PublicProductType {
  id: number
  kind: ProductKindType
  key: string
  name: string
  description?: string | null
  /** Descripción larga en HTML; el backend la devuelve ya saneada. */
  content?: string | null
  steps: { title: string; description?: string | null }[]
  price: number
  compareAtPrice?: number | null
  unit: string
  images: string[]
  tags: string[]
  details: string[]
  specs: ProductSpecType[]
  trackInventory: boolean
  category?: { id: number; label: string } | null
  attributes: { name: string; values: string[] }[]
  prices: PublicPriceRowType[]
  variants: PublicVariantType[]
}

export interface PublicCompanyType {
  name: string
  slug: string
  /**
   * Sólo dígitos, listo para `wa.me`. Nulo cuando la empresa no ha configurado
   * el teléfono del emisor de cotizaciones: entonces no se enseña el botón.
   */
  whatsapp?: string | null
}

export interface QuoteRequestType {
  name: string
  email?: string | null
  phone?: string | null
  quantity?: number
  variantId?: number | null
  message?: string | null
}

export interface PublicCatalogParamsType {
  page?: number
  limit?: number
  kind?: ProductKindType
  categoryId?: number
  q?: string
  /** Características, como `color:rojo,talla:42`. */
  specs?: string
}

export const catalogService = {
  async list(empresa: string, params?: PublicCatalogParamsType) {
    const { data } = await api.get(`/public/catalog/${empresa}`, { params })
    return data.data as {
      items: PublicProductType[]
      company: PublicCompanyType
      meta: { page: number; perPage: number; total: number; totalPages: number }
    }
  },

  async facets(empresa: string, params?: { kind?: ProductKindType }) {
    const { data } = await api.get(`/public/catalog/${empresa}/facets`, { params })
    return data.data as ProductFacetGroupType[]
  },

  async get(empresa: string, key: string) {
    const { data } = await api.get(`/public/catalog/${empresa}/${key}`)
    return data.data as {
      product: PublicProductType
      related: PublicProductType[]
      company: PublicCompanyType
    }
  },

  async requestQuote(empresa: string, key: string, payload: QuoteRequestType) {
    const { data } = await api.post(`/public/catalog/${empresa}/${key}/quote`, payload)
    return data as { message: string; data: { number?: string } }
  }
}

/**
 * Precio efectivo para la variante y cantidad dadas.
 * Usa toda la tabla `prices` (tarifas + tramos) y respeta:
 * - variante: la fila de esa variante gana si hay tramo que quepa.
 * - volumen: a mayor cantidad, mejor tramo (incluso cambiando de tarifa).
 * Si una cantidad activa un mayorista, lo aplica auto sin tener que elegir tarifa.
 */
export const variantPrice = (product: PublicProductType, variant?: PublicVariantType | null, quantity = 1): number => {
  const prices = product.prices ?? []
  if (prices.length === 0) return variant?.price != null ? variant.price : product.price

  const q = Math.max(1, quantity)
  const variantId = variant?.id ?? null

  // Candidatas que caben en la cantidad
  const applicable = prices.filter(p => Number(p.minQuantity) <= q)

  // Mejor tramo de la variante (si hay)
  let bestVariant: PublicPriceRowType | undefined
  if (variantId != null) {
    const forVar = applicable.filter(p => p.variantId === variantId)
    if (forVar.length > 0) bestVariant = forVar.sort((a, b) => Number(b.minQuantity) - Number(a.minQuantity))[0]
  }

  // Mejor tramo general
  const forGeneral = applicable.filter(p => p.variantId == null)
  let bestGeneral: PublicPriceRowType | undefined
  if (forGeneral.length > 0) bestGeneral = forGeneral.sort((a, b) => Number(b.minQuantity) - Number(a.minQuantity))[0]

  // Variante gana sobre general, pero el volumen mayorista sí debe beneficiar:
  // - q baja (1-5): premium de color se respeta (259900 para Azul vs 249900 base)
  // - q alta (6+): si el tramo general es mayorista/distribuidor y más barato, aplica.
  const vPrice = bestVariant ? Number(bestVariant.amount) : null
  const gPrice = bestGeneral ? Number(bestGeneral.amount) : null

  if (vPrice != null) {
    if (gPrice != null) {
      const isVolumeTier = Number(bestGeneral!.minQuantity) > 1
      if (isVolumeTier && gPrice < vPrice) return gPrice
    }
    return vPrice
  }
  if (gPrice != null) return gPrice
  return variant?.price != null ? variant.price : product.price
}

/** Tarifa/tramo activo para mostrar el badge "Mayorista / Distribuidor". */
export const activeTier = (product: PublicProductType, variant?: PublicVariantType | null, quantity = 1): PublicPriceRowType | undefined => {
  const prices = product.prices ?? []
  if (prices.length === 0) return undefined
  const q = Math.max(1, quantity)
  const variantId = variant?.id ?? null
  const applicable = prices.filter(p => Number(p.minQuantity) <= q)
  if (variantId != null) {
    const forVar = applicable.filter(p => p.variantId === variantId).sort((a, b) => Number(b.minQuantity) - Number(a.minQuantity))[0]
    if (forVar) {
      const g = applicable.filter(p => p.variantId == null).sort((a, b) => Number(b.minQuantity) - Number(a.minQuantity))[0]
      if (g && Number(g.amount) < Number(forVar.amount)) return g
      return forVar
    }
  }
  return applicable.filter(p => p.variantId == null).sort((a, b) => Number(b.minQuantity) - Number(a.minQuantity))[0]
}

/** "Talla 42 · Negro" a partir de las opciones de una variante. */
export const optionsLabel = (variant: PublicVariantType): string =>
  Object.values(variant.options ?? {})
    .filter(Boolean)
    .join(' · ')

/** La variante que casa exactamente con la selección de atributos. */
export const findVariant = (product: PublicProductType, selection: Record<string, string>): PublicVariantType | undefined => {
  const names = product.attributes.map(a => a.name)
  if (names.length === 0) return undefined
  return product.variants.find(v => names.every(name => v.options?.[name] === selection[name]))
}
